<template>
  <ankh-list>
    <template #btn-group>
      <el-button type="primary" @click.native="handleAdd">{{$t('新增')}}</el-button>
      <el-button @click.native="handleBatchDelete">{{$t('批量删除')}}</el-button>
    </template>

    <!-- 搜索表单区：遵循系统统一表单间距、大小与布局 -->
    <template #search-form>
      <!-- 名称搜索 -->
      <el-form-item :label="$t('名称')">
        <el-input v-model="searchForm.name" clearable :placeholder="$t('名称')" />
      </el-form-item>
      <!-- 时间范围 -->
      <el-form-item :label="$t('创建时间')">
        <date-time-range-picker :start-time.sync="searchForm.startTime" :end-time.sync="searchForm.endTime" value-format="timestamp" />
      </el-form-item>
      <!-- 操作按钮与统一交互 -->
      <el-form-item>
        <el-button type="primary" size="small" @click="$refs.list.handleSearch()">{{ $t('搜索') }}</el-button>
        <el-button size="small" @click="$refs.list.reset()">{{ $t('重置') }}</el-button>
      </el-form-item>
    </template>

    <!-- 表格区：系统封装TableList，接入高度传递、选择与查询事件 -->
    <template #table="{height}">
      <table-list
          ref="list"
          :height="height"
          @query="handleSearch"
          :search-params="searchForm"
          :data="tableData"
          @selection-change="selection = $event">

        <el-table-column type="selection" width="50" />
        <el-table-column type="index" :label="$t('序号')" width="60" fixed />

        <el-table-column prop="name" :label="$t('名称')" min-width="180" />
        <el-table-column prop="description" :label="$t('描述')" min-width="220" show-overflow-tooltip />
        <el-table-column :label="$t('创建时间')" min-width="180">
          <template v-slot="{row}">
            {{ row.createTime | formatDate }}
          </template>
        </el-table-column>

        <el-table-column :label="$t('操作')" width="350" fixed="right" align="center">
          <template v-slot="{row}">
            <div class="fb-link-flex">
              <el-link :underline="false" type="primary" @click="handleOpenRecommendConfig(row)">{{ $t('推荐值配置') }}</el-link>
              <el-link :underline="false" type="primary" @click="handleOpenWorkloadConfig(row)">{{ $t('劳动量配置') }}</el-link>
              <el-link :underline="false" type="primary" @click="$router.push('/bmi-manage/list?code=' + row.code)">{{ $t('BMI配置') }}</el-link>
              <el-link :underline="false" type="primary" @click="$router.push('/food/el-manage?code=' + row.code)">{{ $t('元素配置') }}</el-link>
              <el-link :underline="false" type="primary" @click="$router.push('/algo/list?code=' + row.code)">{{ $t('算法配置') }}</el-link>
              <el-link :underline="false" type="primary" @click="handleEdit(row)">{{ $t('编辑') }}</el-link>
              <el-link :underline="false" type="danger" @click="handleDelete(row)">{{ $t('删除') }}</el-link>
            </div>
          </template>
        </el-table-column>

      </table-list>
    </template>

    <!-- 弹窗区：预留新增/编辑表单弹窗位点，保持统一行为与间距 -->
    <template #other>
      <el-dialog :visible.sync="showDialog" :title="dialogTitle" :close-on-click-modal="false" width="520px" @close="resetDialog" class="recommend-dialog">
        <el-form :model="form" :rules="rules" ref="form" label-width="80px" class="compact-form">
          <el-form-item :label="$t('名称')" prop="name">
            <el-input v-model="form.name" :placeholder="$t('名称')" />
          </el-form-item>
          <el-form-item :label="$t('描述')" prop="description">
            <el-input type="textarea" v-model="form.description" :rows="4" :placeholder="$t('描述')" />
          </el-form-item>
        </el-form>
        <span slot="footer" class="dialog-footer">
          <el-button @click="showDialog = false">{{ $t('取消') }}</el-button>
          <el-button type="primary" @click="handleSubmit">{{ $t('确定') }}</el-button>
        </span>
      </el-dialog>
    </template>
  </ankh-list>

</template>

<script>
import AnkhList from '@/components/list/AnkhList.vue'
import TableList from '@/components/table-list/TableList'
import { request, requestByTip } from '@/config/request'
import DateTimeRangePicker from "@/components/time/DateTimeRangePicker.vue";

export default {
  name: 'RecommendManageList',
  components: {DateTimeRangePicker, AnkhList, TableList },
  data() {
    return {
      // 搜索表单
      searchForm: {
        name: '',
        startCreateTime: undefined,
        endCreateTime: undefined,
      },
      // 表格数据
      tableData: [],
      // 选择项
      selection: [],
      // 弹窗
      showDialog: false,
      isEdit: false,
      form: {
        code: '',
        name: '',
        description: '',
      },
      // 表单校验规则
      rules: {
        name: [{ required: true, message: this.$t('请输入名称'), trigger: 'blur' }],
        code: [{ required: true, message: this.$t('请输入标识'), trigger: 'blur' }],
      },
    }
  },
  methods: {
    // 查询列表：保持与系统TableList事件对接
    async handleSearch(params, setTotal) {
      try {
        // 合并搜索与分页参数；request封装会过滤空值
        const query = Object.assign({}, params)
        const resp = await request(this.$api.recommend.list, query)
        this.tableData = resp.recommends || []
        setTotal(resp.pager?.itemCount);
      } catch (e) {
        // 静默失败，交由request内部提示
      }
    },
    // 新增
    handleAdd() {
      this.isEdit = false
      this.showDialog = true
      this.form = { code: '', name: '', description: '' }
    },
    // 编辑
    handleEdit(row) {
      this.isEdit = true
      this.showDialog = true
      this.form = Object.assign({}, row)
    },
    // 推荐值配置
    handleOpenRecommendConfig(row) {
      // 跳转到推荐值配置列表
      this.$router.push('/recommend-config/view?code=' + row.code)
    },
    // 劳动量配置
    handleOpenWorkloadConfig(row) {
      // 跳转到劳动量管理列表
      this.$router.push('/workload-manage/list?code=' + row.code)
    },
    // 删除
    async handleDelete(row) {
      try {
        await requestByTip(this.$api.recommend.delete, { code: row.code })
        this.$message.success(this.$t('删除成功'))
        this.$refs.list && this.$refs.list.handleSearch && this.$refs.list.handleSearch()
      } catch (e) {
        console.error(e)
      }
    },
    // 批量删除
    async handleBatchDelete() {
      if (!this.selection || !this.selection.length) {
        this.$message.warning(this.$t('请选择需要删除的记录'))
        return
      }
      try {
        const codes = this.selection.map(it => it.code)
        await requestByTip(
          this.$api.recommend.batchDelete,
            codes,
          { isJson: true },
          { message: this.$t('您现在正在进行批量删除操作, 是否继续?') }
        )
        this.$message.success(this.$t('删除成功'))
        this.$refs.list && this.$refs.list.handleSearch && this.$refs.list.handleSearch()
      } catch (e) {
        console.error(e)
      }
    },
    // 提交（新增/编辑）
    async handleSubmit() {
      try {
        await this.$refs.form.validate()
        const api = this.isEdit ? this.$api.recommend.update : this.$api.recommend.add
        await request(api, this.form)
        this.$message.success(this.$t('保存成功'))
        this.showDialog = false
        this.$refs.list && this.$refs.list.handleSearch && this.$refs.list.handleSearch()
      } catch (e) {
        console.error(e)
      }
    },
    // 重置弹窗
    resetDialog() {
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate && this.$refs.form.clearValidate()
      })
    },
  },
  computed: {
    dialogTitle() {
      return this.isEdit ? this.$t('编辑') : this.$t('新增')
    }
  }
}
</script>

<style scoped>
.text-muted {
  color: #909399;
  margin-left: 4px;
}
</style>


